<template>
  <!-- The sidebar (MacMain, d37, d31): "Hide sidebar" in the 52 px top
       band, the places by section with their counts, and the footer with
       you and Settings on every pane. 210 wide at pointer density, 280 at
       touch. -->
  <nav class="sidebar" aria-label="Sidebar">
    <div class="sb-top">
      <button type="button" class="sb-icon-btn" :title="`Hide sidebar (${keyLabel})`" aria-label="Hide sidebar" @click="hide">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 5h16v14H4zM9 5v14" /></svg>
      </button>
    </div>
    <div class="sb-scroll">
      <template v-for="section in sections" :key="section.title">
        <div class="sb-section">{{ section.title }}</div>
        <div class="sb-group">
          <SidebarRow
            v-for="p in section.places"
            :key="p.id"
            :place="p"
            :selected="selectedPlace === p.id"
            :count="countFor(p, counts)"
          />
        </div>
      </template>
    </div>
    <div class="sb-foot">
      <router-link v-if="person" :to="`/users/${encodeURIComponent(person.username)}`" class="sb-me" title="You">
        <PersonDot class="sb-avatar" :name="person.name" color="var(--cl-accent)" :picture="person.picture" :size="26" />
        <span class="sb-name">{{ person.name }}</span>
      </router-link>
      <router-link to="/settings" class="sb-icon-btn" title="Settings" aria-label="Settings">
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 7h9M17 7h3M4 17h3M11 17h9M15 5v4M9 15v4" /></svg>
      </router-link>
    </div>
  </nav>
</template>

<script>
import PersonDot from "@/components/kit/PersonDot.vue";
import SidebarRow from "./SidebarRow.vue";
import { countFor, sidebarSections } from "./places.js";
import { setCollapsed, shellState } from "./shellState.js";
import { sidebarKeyLabel } from "./shortcuts.js";
import { me } from "./me.js";
import sessionLists from "@/stores/sessionLists.js";

export default {
  name: "Sidebar",
  components: { PersonDot, SidebarRow },
  props: {
    // The place selected: the pane's, or the last one under an overlay.
    selectedPlace: { type: String, default: null },
  },
  computed: {
    counts() {
      return { ...sessionLists.counts.value, inbox: shellState.badges.inbox };
    },
    sections() {
      return sidebarSections(this.counts);
    },
    person() {
      return me.value;
    },
    keyLabel() {
      return sidebarKeyLabel();
    },
  },
  methods: {
    countFor,
    hide() {
      setCollapsed(true);
    },
  },
};
</script>

<style scoped>
.sidebar {
  position: sticky;
  top: 0;
  align-self: flex-start;
  height: 100dvh;
  width: 210px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  background: var(--cl-sidebar);
  border-right: 1px solid var(--border-subtle);
  box-sizing: border-box;
}
:root[data-density="touch"] .sidebar {
  width: 280px;
}
.sb-top {
  height: 52px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 0 10px;
}
.sb-icon-btn {
  width: 30px;
  height: 30px;
  flex-shrink: 0;
  border: none;
  border-radius: 7px;
  background: transparent;
  color: var(--cl-label-2);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}
.sb-icon-btn:hover {
  background: var(--cl-hover);
  color: var(--cl-label-2);
}
.sb-scroll {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding-bottom: 8px;
}
.sb-section {
  padding: 12px 18px 5px;
  font: 600 11px/13px var(--cl-font);
  color: var(--cl-label-3);
}
.sb-group {
  padding: 0 8px;
  display: flex;
  flex-direction: column;
  gap: 1px;
}
.sb-foot {
  height: 52px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 10px 0 14px;
  border-top: 1px solid var(--border-subtle);
}
.sb-me {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 10px;
  text-decoration: none;
  color: var(--cl-label);
}
.sb-avatar :deep(*) {
  font-size: 10px;
}
.sb-me:hover {
  color: var(--cl-label);
}
.sb-name {
  min-width: 0;
  font: 600 13px/16px var(--cl-font);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
